import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  Hidden,
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  Button,
} from '@mui/material';
import api from '../../utils/functions/api';
import addCommas from '../../utils/functions/commas';
import CardPaymentHistory from '../Card/CardPaymentHistory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Swal from 'sweetalert2';
import { swalErrorTimer, swalLoading } from '../../helpers/swalHelper';
import PaymentListApi from '../../service/payment.api';
import ReceiptDetailDialog from '../dialogs/payment/ReceiptDetailDialog';

function TablePaymentHistory({
  paymentList,
  rowsPerPage,
  page,
  total,
  sort,
  setPage,
  setRowsPerPage,
  setSort,
}) {
  const [type, setType] = useState('receipt');
  const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
  const [showPDF, setShowPDF] = useState('');
  
  // console.log('rows', paymentList);
  const handleSort = (column) => {
    if (sort.column === column) {
      setSort({
        ...sort,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      });
      setPage(0);
    } else {
      setSort({ direction: 'asc', column });
      setPage(0);
    }
  };

  const handleShowReceiptDialog = async (orderId) => {
    swalLoading({ title: 'กำลังเปิดไฟล์ PDF...' });
    try {
      const resp = await PaymentListApi.getReceiptApi(orderId);

      const base64 = resp?.base64;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);

      Swal.close();
      setType('receipt');
      setShowPDF(pdfUrl);
      setOpenReceiptDialog(true);
    } catch (error) {
      console.log("เกิดข้อผิดพลาด", error);
      swalErrorTimer({ text: `ไม่สามารถเปิดไฟล์ PDF ได้!` })
    }
  }

  const closeReceiptDialog = () => {
    setOpenReceiptDialog(false);
  }

  return (
    <>
      <Hidden lgDown>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: '100%' }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="center">เลขที่ใบแจ้งหนี้</TableCell>
                <TableCell align="center">รายการ</TableCell>
                <TableCell align="center">เลขที่อ้างอิง</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sort.column === 'pay_date'}
                    direction={sort.direction}
                    onClick={() => handleSort('pay_date')}
                  >
                    วันที่ชำระเงิน
                  </TableSortLabel>
                </TableCell>

                <TableCell align="right">
                  <TableSortLabel
                    active={sort.column === 'total_amount'}
                    direction={sort.direction}
                    onClick={() => handleSort('total_amount')}
                  >
                    จำนวนเงิน
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">เพิ่มเติม</TableCell>
              </TableRow>
            </TableHead>
            {_.size(paymentList) ? (
              <TableBody>
                {paymentList.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">{row.invoice_no}</TableCell>
                    <TableCell align="center">{row.descriptions}</TableCell>
                    <TableCell align="center">
                      {row.payment_reference_id}
                    </TableCell>
                    <TableCell align="center">
                      {moment(row.pay_date).utc().format('YYYY-MM-DD')}
                    </TableCell>

                    <TableCell align="right">
                      {addCommas(row.total_amount, 2)}
                    </TableCell>
                    <TableCell align='center'>
                      {/* <Tooltip title="ดูใบเสร็จชำระเงิน" arrow>
                        <span>
                          <IconButton
                            onClick={() => handleShowReceiptDialog(row.order_id)}
                          >
                            <ReceiptLongIcon
                              color='primary'
                            />
                          </IconButton>
                        </span>
                      </Tooltip> */}
                      <Button 
                        variant='contained' 
                        size='small' 
                        sx={{ color: '#fff' }}  
                        onClick={() => handleShowReceiptDialog(row.order_id)}
                      >
                        ใบเสร็จชำระเงิน
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <div>ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            rowsPerPage={rowsPerPage}
            page={page}
            count={total}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Hidden>

      <Hidden lgUp>
        <Grid container spacing={2}>
          {paymentList.map((row, index) => (
            <Grid item xs={16} md={6} key={index}>
              <CardPaymentHistory 
                row={row}
                onShowReceipt={handleShowReceiptDialog}
              />
            </Grid>
          ))}
          <Grid item md={12} sx={{ justifyContent: 'center', display: 'flex', mb: 6 }}>
            <TablePagination
              sx={{
                width: 'auto',
                marginRight: '8px',
                '& .MuiTypography-caption': { display: 'none' },
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              rowsPerPage={rowsPerPage}
              page={page}
              count={total}
              labelRowsPerPage="Rows"
              onPageChange={(event, newPage) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Grid>
        </Grid>
      </Hidden>
      
      <ReceiptDetailDialog
        open={openReceiptDialog}
        onClose={closeReceiptDialog}
        URL={showPDF}
        type={type}
      />
    </>
  );
}

export default TablePaymentHistory;
